import { MouseEventHandler, useCallback } from 'react'
import { useIsMutating } from 'react-query'
import {
  Box,
  Step as MuiStep,
  stepConnectorClasses,
  StepIconProps,
  StepLabel,
  stepLabelClasses,
  Stepper as MuiStepper,
  styled,
  SxProps,
  Theme,
} from '@mui/material'
import clsx from 'clsx'
import { findIndex } from 'lodash'

import { Page } from 'common/interfaces'
import { useSessionDispatch, useSessionState } from 'context/Session'
import { useFlow } from 'utils/flow'
import { getSteps } from 'utils/getSteps'
import { useCurrentPage } from 'utils/hook'

const styles: Record<string, SxProps<Theme>> = {
  box: {
    flex: '1 1 auto',
    overflowY: 'auto',
  },
  stepper: {
    overflowY: 'auto',
    [`& .${stepConnectorClasses.root}`]: {
      marginLeft: '10px',
    },
    [`& .${stepConnectorClasses.lineVertical}`]: {
      minHeight: '32px',
      borderLeftStyle: 'dashed',
      borderColor: '#B4B4B4',
    },
  },
  step: {
    [`& .${stepLabelClasses.root}`]: {
      padding: 0,
    },
    [`&.${stepLabelClasses.completed}`]: {
      cursor: 'pointer',
      [`& .${stepLabelClasses.label}`]: {
        fontWeight: 400,
      },
      [`& + .${stepConnectorClasses.root} .${stepConnectorClasses.lineVertical}`]: {
        borderLeftStyle: 'solid',
      },
    },
    [`&:not(.${stepLabelClasses.completed})`]: {
      cursor: 'not-allowed',
    },
    [`& .${stepLabelClasses.disabled}`]: {
      cursor: 'not-allowed',
    },
    [`& .${stepLabelClasses.iconContainer}`]: {
      paddingRight: '12px',
    },
    [`& .${stepLabelClasses.label}.${stepLabelClasses.active}`]: {
      fontWeight: 600,
    },
  },
}

type StepIconCustomProps = StepIconProps & {
  isFinalStep: boolean
}

const StepIconWrapper = styled('span')<Pick<StepIconCustomProps, 'completed' | 'isFinalStep'>>(
  ({ theme, completed, isFinalStep }) => ({
    width: '20px',
    height: '20px',
    padding: '0 6px',
    border: `2px solid ${completed ? theme.palette.success.light : '#BDBDBD'}`,
    borderRadius: '50%',
    backgroundColor: completed ? theme.palette.success.light : 'white',
    ...(completed && {
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='11' height='9' viewBox='0 0 11 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 4L4 7L10 1' stroke='white' stroke-width='2'/%3E%3C/svg%3E%0A")`,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }),
    ...(!isFinalStep && {
      '&.circle-icon--active': {
        borderColor: theme.palette.primary.main,
      },
    }),
  })
)

const StepIcon = ({ isFinalStep, active, completed }: StepIconCustomProps): JSX.Element => (
  <StepIconWrapper
    className={clsx('circle-icon', { 'circle-icon--active': isFinalStep || active })}
    completed={completed! || isFinalStep}
    isFinalStep={isFinalStep}
  />
)

export default function Stepper(): JSX.Element {
  const { box, stepper, step } = styles
  const { data: flow } = useFlow()
  const { pages } = flow!
  const currentPage = useCurrentPage()
  const { longest_path_length: longestPathLength } = currentPage
  const { isSubmitted, path } = useSessionState()
  const [dispatch] = useSessionDispatch()
  const isMutatingNumber = useIsMutating()
  const isMutating = !(isMutatingNumber === 0)

  const steps = getSteps([...path, currentPage.id], pages)

  const activeStep = findIndex(steps, { name: currentPage.name })

  const handleClick = useCallback(
    (id: Page['id']): MouseEventHandler<HTMLDivElement> | undefined => {
      return path.includes(id) && !isSubmitted
        ? () => dispatch({ type: 'goTo', step: id })
        : undefined
    },
    [isSubmitted, currentPage.id]
  )
  return (
    <Box className="main-sidebar-stepper" sx={box}>
      <MuiStepper orientation="vertical" sx={stepper} activeStep={activeStep}>
        {steps.map(({ id, title }) => (
          <MuiStep
            {...(!(isMutating || isSubmitted) && { onClick: handleClick(id) })}
            sx={step}
            disabled={isMutating || isSubmitted}
            key={id}
          >
            <StepLabel
              StepIconComponent={(props) => (
                <StepIcon isFinalStep={longestPathLength === 0} {...props} />
              )}
            >
              {title}
            </StepLabel>
          </MuiStep>
        ))}
      </MuiStepper>
    </Box>
  )
}
